import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Star, Plus, ForkKnife, House, Camera, Tree, Sun, Bank, Airplane, Tent,
    Trash, PencilSimple, ShieldCheck, ThumbsUp, BookmarkSimple, Sparkle, Flag,
    MagnifyingGlass, X, MapPin, CaretLeft, CaretRight, PaperPlaneTilt
} from "@phosphor-icons/react";
import { reviewsApi, bucketApi, reportsApi, toAbsolute, uploadsApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { WORLD_COUNTRIES } from "@/pages/Dashboard";
import PhotoLightbox from "@/components/PhotoLightbox";
import { usePassportShare } from "@/components/messages/PassportShareProvider";

// ─── helpers ────────────────────────────────────────────────────────────────
function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.round((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
}

const CATEGORIES_CONFIG = [
    { key: "restaurant", icon: ForkKnife,  labelKey: "reviewsPanel.categories.restaurant", defaultLabel: "Restaurants" },
    { key: "hotel",      icon: House,       labelKey: "reviewsPanel.categories.hotel",      defaultLabel: "Hotels"       },
    { key: "attraction", icon: Camera,      labelKey: "reviewsPanel.categories.attraction", defaultLabel: "Attractions"  },
    { key: "park",       icon: Tree,        labelKey: "reviewsPanel.categories.park",        defaultLabel: "Parks"       },
    { key: "beach",      icon: Sun,         labelKey: "reviewsPanel.categories.beach",       defaultLabel: "Beaches"     },
    { key: "museum",     icon: Bank,        labelKey: "reviewsPanel.categories.museum",      defaultLabel: "Museums"     },
    { key: "airport",    icon: Airplane,    labelKey: "reviewsPanel.categories.airport",     defaultLabel: "Airports"    },
    { key: "campground", icon: Tent,        labelKey: "reviewsPanel.categories.campground",  defaultLabel: "Campgrounds" },
];

const PRICING_OPTIONS = ["$", "$$", "$$$", "$$$$"];
const SORT_OPTIONS = [
    { value: "newest",        label: "Newest"         },
    { value: "highest_rated", label: "Highest Rated"  },
    { value: "most_helpful",  label: "Most Helpful"   },
];

// ─── Report Modal ───────────────────────────────────────────────────────────
function ReportModal({ reviewId, onClose }) {
    const [category, setCategory] = useState("spam");
    const [detail, setDetail]     = useState("");
    const [busy,   setBusy]       = useState(false);
    const cats = ["spam", "inappropriate", "misleading", "harassment"];
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await reportsApi.submit({ target_type: "review", target_id: reviewId, category, detail });
            toast.success("Review reported — thank you.");
            onClose();
        } catch { toast.error("Could not submit report."); }
        finally { setBusy(false); }
    };
    return ReactDOM.createPortal(
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }}
                onClick={onClose}
            >
                <motion.form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                    initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                    className="rounded-3xl max-w-sm w-full p-7"
                    style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}
                >
                    <div className="overline mb-2">Report Review</div>
                    <h3 className="font-serif text-2xl text-white mb-5">Flag this content</h3>
                    <div className="space-y-2 mb-4">
                        {cats.map((c) => (
                            <label key={c} className="flex items-center gap-3 cursor-pointer">
                                <input type="radio" name="rc" value={c} checked={category === c}
                                    onChange={() => setCategory(c)}
                                    className="accent-yellow-500"
                                />
                                <span className="text-white/80 capitalize text-sm">{c}</span>
                            </label>
                        ))}
                    </div>
                    <textarea value={detail} onChange={(e) => setDetail(e.target.value)}
                        placeholder="Additional details (optional)"
                        className="w-full rounded-xl text-sm p-3 mb-4 resize-none"
                        style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.08)", color: "white" }}
                        rows={3}
                    />
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose}
                            className="flex-1 py-2.5 rounded-full text-sm text-white/60 border border-white/10 hover:border-white/30 transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={busy}
                            className="flex-1 py-2.5 rounded-full text-sm font-semibold bg-red-600/80 hover:bg-red-600 transition-colors text-white disabled:opacity-50">
                            {busy ? "Sending…" : "Submit Report"}
                        </button>
                    </div>
                </motion.form>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
}

// ─── Review Card ────────────────────────────────────────────────────────────
export function ReviewCard({ r, user, onEdit, onDelete, onUpdate }) {
    const navigate    = useNavigate();
    const { openPassportShare } = usePassportShare();
    const isOwner     = user?.id === r.user_id;
    const hasVoted    = (r.helpful_by || []).includes(user?.id);
    const [helpfulCount, setHelpfulCount] = useState(r.helpful_count || 0);
    const [voted,        setVoted]        = useState(hasVoted);
    const [savedLocal,   setSavedLocal]   = useState(false);
    const [reportTarget, setReportTarget] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [photoIdx, setPhotoIdx] = useState(0);

    const photos = r.photos?.length ? r.photos : (r.photo_url ? [r.photo_url] : []);

    const nextPhoto = (e) => {
        e.stopPropagation();
        setPhotoIdx((prev) => (prev + 1) % photos.length);
    };

    const prevPhoto = (e) => {
        e.stopPropagation();
        setPhotoIdx((prev) => (prev - 1 + photos.length) % photos.length);
    };

    const handleHelpful = async () => {
        if (!user) { toast.error("Sign in to vote"); return; }
        const prev = voted;
        setVoted(!prev);
        setHelpfulCount((c) => c + (prev ? -1 : 1));
        try {
            const res = await reviewsApi.helpful(r.id);
            setHelpfulCount(res.helpful_count);
            setVoted(res.has_voted);
            onUpdate?.(r.id, { helpful_count: res.helpful_count, helpful_by: res.has_voted ? [...(r.helpful_by || []), user.id] : (r.helpful_by || []).filter((x) => x !== user.id) });
        } catch {
            setVoted(prev);
            setHelpfulCount((c) => c + (prev ? 1 : -1));
            toast.error("Could not register vote");
        }
    };

    const handleBucketSave = async () => {
        if (!user) { toast.error("Sign in to save places"); return; }
        try {
            await bucketApi.create({
                destination: `${r.place_name} (${r.city})`,
                country: r.country,
                note: r.tip,
                image_url: toAbsolute(photos[0]) || undefined,
            });
            setSavedLocal(true);
            toast.success("Saved to Bucket List!");
        } catch { toast.error("Could not save to Bucket List"); }
    };

    const handleConcierge = () => {
        const prefill = encodeURIComponent(`Build a 1-day itinerary around ${r.place_name} in ${r.city}, ${r.country}`);
        navigate(`/concierge?prefill=${prefill}`);
    };

    const handleShareInMessages = () => {
        openPassportShare({ shareRef: { entity_type: "review", entity_id: r.id }, sourceLabel: "this Review" });
    };

    return (
        <div className="rounded-2xl overflow-hidden card-hover" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`review-${r.id}`}>
            {/* Photo strip */}
            {photos.length > 0 && (
                <>
                <div className="aspect-[16/9] overflow-hidden relative cursor-pointer group bg-black" onClick={() => setLightboxOpen(true)}>
                    <div 
                        className="absolute inset-0 bg-center bg-cover blur-xl scale-110 opacity-50" 
                        style={{ backgroundImage: `url(${toAbsolute(photos[photoIdx])})` }} 
                    />
                    <img src={toAbsolute(photos[photoIdx])} alt={r.place_name} className="relative w-full h-full object-contain transition-opacity group-hover:opacity-90 z-10" />
                    {photos.length > 1 && (
                        <>
                            <button onClick={prevPhoto} className="absolute z-20 left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60">
                                <CaretLeft size={16} />
                            </button>
                            <button onClick={nextPhoto} className="absolute z-20 right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60">
                                <CaretRight size={16} />
                            </button>
                            <span className="absolute z-20 bottom-2 right-2 text-[11px] font-semibold px-2 py-1 rounded-full"
                                style={{ background: "rgba(5,10,17,0.75)", color: "var(--gold)" }}>
                                {photoIdx + 1} / {photos.length}
                            </span>
                        </>
                    )}
                </div>
                <PhotoLightbox open={lightboxOpen} photos={photos} initialIndex={photoIdx} onClose={() => setLightboxOpen(false)} />
                </>
            )}

            <div className="p-5">
                {/* Header row */}
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{r.category} · {r.city}, {r.country}</div>
                        <div className="font-serif text-xl text-white leading-tight mt-1">{r.place_name}</div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="flex">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} size={12} weight={i < r.rating ? "fill" : "regular"} color={i < r.rating ? "#D4AF37" : "rgba(255,255,255,0.3)"} />
                            ))}
                        </div>
                    </div>
                </div>

                <p className="text-white/70 text-sm leading-relaxed mb-3">{r.tip}</p>

                {/* Metadata badges */}
                <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.15em] text-white/50 mb-4">
                    {r.pricing && <span>{r.pricing}</span>}
                    {r.accessibility && <span className="max-w-full break-words leading-relaxed whitespace-normal">{r.pricing ? "· " : ""}♿ {r.accessibility}</span>}
                    {r.safety && (
                        <span className="flex items-center gap-1 max-w-full break-words leading-relaxed whitespace-normal"
                            style={{ color: safetyColor(r.safety) }}>
                            {r.pricing || r.accessibility ? "· " : ""}<ShieldCheck size={12} className="shrink-0" /> {r.safety}
                        </span>
                    )}
                </div>

                {/* Action bar */}
                <div className="flex items-center gap-2 flex-wrap pt-3 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                    {/* Author */}
                    <Link to={`/profile/${r.user_handle}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity mr-auto">
                        {r.user_avatar && <img src={toAbsolute(r.user_avatar)} alt="" className="w-6 h-6 rounded-full object-cover" />}
                        <span className="text-xs" style={{ color: "var(--gold)" }}>@{r.user_handle}</span>
                    </Link>
                    <span className="text-[10px] text-white/40 uppercase tracking-widest">{timeAgo(r.created_at)}</span>

                    {/* Helpful */}
                    <button onClick={handleHelpful}
                        className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-full transition-all"
                        style={{
                            background: voted ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.04)",
                            border: `1px solid ${voted ? "var(--gold)" : "rgba(255,255,255,0.08)"}`,
                            color: voted ? "var(--gold)" : "rgba(255,255,255,0.5)",
                        }}
                        title="Mark as helpful"
                    >
                        <ThumbsUp size={11} weight={voted ? "fill" : "regular"} />
                        {helpfulCount > 0 && <span>{helpfulCount}</span>}
                    </button>

                    {/* Bucket List save */}
                    <button onClick={handleBucketSave} disabled={savedLocal}
                        className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-full transition-all"
                        style={{
                            background: savedLocal ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.04)",
                            border: `1px solid ${savedLocal ? "var(--gold)" : "rgba(255,255,255,0.08)"}`,
                            color: savedLocal ? "var(--gold)" : "rgba(255,255,255,0.5)",
                        }}
                        title="Save to Bucket List"
                    >
                        <BookmarkSimple size={11} weight={savedLocal ? "fill" : "regular"} />
                    </button>

                    {/* Plan with AI */}
                    <button onClick={handleConcierge}
                        className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-full transition-all"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)" }}
                        title="Plan with AI Concierge"
                        data-testid={`review-plan-${r.id}`}
                    >
                        <Sparkle size={11} />
                    </button>

                    <button onClick={handleShareInMessages}
                        className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-full transition-all"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)" }}
                        title="Send in Passport Messages"
                        data-testid={`review-send-${r.id}`}
                    >
                        <PaperPlaneTilt size={11} />
                    </button>

                    {/* Report */}
                    {!isOwner && (
                        <button onClick={() => setReportTarget(r.id)}
                            className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-full transition-all"
                            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)" }}
                            title="Report this review"
                            data-testid={`review-report-${r.id}`}
                        >
                            <Flag size={11} />
                        </button>
                    )}
                </div>
            </div>

            {reportTarget && <ReportModal reviewId={reportTarget} onClose={() => setReportTarget(null)} />}
        </div>
    );
}

// ─── ReviewsPanel ───────────────────────────────────────────────────────────
export default function ReviewsPanel() {
    const { t }   = useTranslation();
    const { user } = useAuth();
    const [items, setItems]               = useState([]);
    const [category, setCategory]         = useState(null);
    const [sort, setSort]                 = useState("newest");
    const [showAdd, setShowAdd]           = useState(false);
    const [editingReview, setEditingReview] = useState(null);

    // search
    const [venueQ,   setVenueQ]   = useState("");
    const [venueInput, setVenueInput] = useState("");
    const [citySearch, setCitySearch] = useState("");
    const [cityInput,  setCityInput]  = useState("");

    const load = useCallback(async () => {
        try {
            const params = {};
            if (category)   params.category = category;
            if (citySearch) params.city      = citySearch;
            if (venueQ)     params.q         = venueQ;
            if (sort)       params.sort      = sort;
            const list = await reviewsApi.list(params);
            setItems(list);
        } catch (e) {}
    }, [category, citySearch, venueQ, sort]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this review?")) return;
        try {
            await reviewsApi.delete(id);
            setItems((prev) => prev.filter((r) => r.id !== id));
            toast.success("Deleted");
        } catch { toast.error("Error deleting review"); }
    };

    const handleUpdate = (id, patch) => {
        setItems((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
    };

    const handleCitySearch  = (e) => { e.preventDefault(); setCitySearch(cityInput.trim()); };
    const clearCity         = ()  => { setCitySearch(""); setCityInput(""); };
    const handleVenueSearch = (e) => { e.preventDefault(); setVenueQ(venueInput.trim()); };
    const clearVenue        = ()  => { setVenueQ(""); setVenueInput(""); };

    return (
        <div data-testid="reviews-panel">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <div className="overline mb-1">{t("reviewsPanel.overline")}</div>
                    <div className="font-serif text-3xl text-white leading-none">{t("reviewsPanel.title")}</div>
                </div>
                <button onClick={() => setShowAdd(true)} className="btn-gold text-sm !py-2.5 !px-5 w-max" data-testid="review-add">
                    <Plus size={14} weight="bold" /> {t("reviewsPanel.writeReview")}
                </button>
            </div>

            {/* Search bar row */}
            <div className="flex flex-col md:flex-row gap-3 mb-6">
                {/* Venue/keyword search */}
                <form onSubmit={handleVenueSearch} className="flex flex-1 items-center gap-2">
                    <div className="relative w-full">
                        <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                            value={venueInput}
                            onChange={(e) => setVenueInput(e.target.value)}
                            placeholder="Search places or reviews…"
                            className="rounded-full text-sm pl-11 pr-10 py-3 w-full transition-all"
                            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", color: "white", outline: "none" }}
                            data-testid="review-venue-search"
                        />
                        {venueQ && (
                            <button type="button" onClick={clearVenue} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"><X size={14} /></button>
                        )}
                    </div>
                    <button type="submit" className="btn-ghost !px-4" data-testid="review-venue-search-btn">Search</button>
                </form>

                {/* City filter */}
                <form onSubmit={handleCitySearch} className="flex flex-1 items-center gap-2">
                    <div className="relative w-full">
                        <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                            value={cityInput}
                            onChange={(e) => setCityInput(e.target.value)}
                            placeholder="Filter by city…"
                            className="rounded-full text-sm pl-11 pr-10 py-3 w-full transition-all"
                            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", color: "white", outline: "none" }}
                            data-testid="review-city-search"
                        />
                        {citySearch && (
                            <button type="button" onClick={clearCity} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"><X size={14} /></button>
                        )}
                    </div>
                    <button type="submit" className="btn-ghost !px-4" data-testid="review-city-search-btn">Filter</button>
                </form>

                {/* Sort */}
                <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="rounded-full text-sm px-4 py-2 cursor-pointer"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", color: "white", outline: "none" }}
                    data-testid="review-sort"
                >
                    {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-slate-900">{o.label}</option>)}
                </select>
            </div>

            {/* Category chips */}
            <div className="flex gap-2 flex-wrap mb-8">
                <button onClick={() => setCategory(null)}
                    className="px-4 py-2 rounded-full text-sm transition-colors"
                    style={{
                        background: !category ? "rgba(212,175,55,0.1)" : "transparent",
                        border: "1px solid " + (!category ? "var(--gold)" : "rgba(255,255,255,0.1)"),
                        color: !category ? "var(--gold)" : "rgba(255,255,255,0.7)",
                    }}
                    data-testid="review-cat-all"
                >
                    {t("reviewsPanel.categories.all")}
                </button>
                {CATEGORIES_CONFIG.map((c) => {
                    const Icon = c.icon;
                    const active = category === c.key;
                    return (
                        <button key={c.key} onClick={() => setCategory(c.key)}
                            className="px-4 py-2 rounded-full text-sm flex items-center gap-2 transition-colors"
                            style={{
                                background: active ? "rgba(212,175,55,0.1)" : "transparent",
                                border: "1px solid " + (active ? "var(--gold)" : "rgba(255,255,255,0.1)"),
                                color: active ? "var(--gold)" : "rgba(255,255,255,0.7)",
                            }}
                            data-testid={`review-cat-${c.key}`}
                        >
                            <Icon size={12} weight={active ? "fill" : "regular"} />
                            {t(c.labelKey, c.defaultLabel)}
                        </button>
                    );
                })}
            </div>

            {/* Grid */}
            <div className="grid md:grid-cols-2 gap-4">
                {items.map((r) => (
                    <ReviewCard
                        key={r.id}
                        r={r}
                        user={user}
                        onEdit={setEditingReview}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                    />
                ))}
                {items.length === 0 && (
                    <div className="col-span-full text-center py-16 rounded-2xl" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                        <div className="text-white/60">{t("reviewsPanel.empty")}</div>
                    </div>
                )}
            </div>

            <ReviewModal
                open={showAdd || !!editingReview}
                initialData={editingReview}
                onClose={() => { setShowAdd(false); setEditingReview(null); }}
                onSaved={() => { setShowAdd(false); setEditingReview(null); load(); }}
            />
        </div>
    );
}

// ─── Photo uploader row ──────────────────────────────────────────────────────
function PhotoUploader({ photos, onChange }) {
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const url = await uploadsApi.upload(file);
            onChange([...photos, url]);  // url is already absolute from uploadsApi.upload
        } catch { toast.error("Photo upload failed"); }
        finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
    };

    const removePhoto = (idx) => onChange(photos.filter((_, i) => i !== idx));

    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-2">
                {photos.map((url, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
                        <img src={toAbsolute(url)} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removePhoto(idx)}
                            className="absolute top-1 right-1 w-5 h-5 rounded-full grid place-items-center"
                            style={{ background: "rgba(5,10,17,0.85)" }}
                        >
                            <X size={10} color="white" />
                        </button>
                        {idx === 0 && (
                            <span className="absolute bottom-0 left-0 right-0 text-center text-[9px] py-0.5" style={{ background: "rgba(212,175,55,0.8)", color: "#050A11" }}>Cover</span>
                        )}
                    </div>
                ))}
                <label className="w-20 h-20 rounded-xl grid place-items-center cursor-pointer flex-col gap-1"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.2)" }}>
                    <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
                    {uploading ? (
                        <div className="text-[10px] text-white/50">…</div>
                    ) : (
                        <>
                            <Plus size={18} color="rgba(255,255,255,0.4)" />
                            <span className="text-[9px] text-white/40">Add Photo</span>
                        </>
                    )}
                </label>
            </div>
            {photos.length === 0 && (
                <p className="text-xs text-red-400/80">At least 1 photo is required.</p>
            )}
        </div>
    );
}

// ─── City autocomplete hook ──────────────────────────────────────────────────
function useCitySuggestions(city, country) {
    const [suggestions, setSuggestions] = useState([]);
    useEffect(() => {
        if (!city || city.length < 2 || !country) { setSuggestions([]); return; }
        const timer = setTimeout(async () => {
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}&limit=6&addressdetails=1`,
                    { headers: { "Accept-Language": "en" } }
                );
                if (res.ok) {
                    const data = await res.json();
                    const names = Array.from(new Set(
                        data.map((item) => {
                            const addr = item.address || {};
                            return addr.city || addr.town || addr.village || addr.suburb || item.display_name.split(",")[0].trim();
                        }).filter(Boolean)
                    ));
                    setSuggestions(names.slice(0, 6));
                }
            } catch { /* ignore network errors */ }
        }, 280);
        return () => clearTimeout(timer);
    }, [city, country]);
    return suggestions;
}

// ─── Safety options ──────────────────────────────────────────────────────────
const SAFETY_OPTIONS = [
    { label: "Very Safe",   color: "#22c55e" },
    { label: "Safe",        color: "#86efac" },
    { label: "Moderate",    color: "#facc15" },
    { label: "Use Caution", color: "#f97316" },
    { label: "High Risk",   color: "#ef4444" },
];
export function safetyColor(level) {
    const opt = SAFETY_OPTIONS.find((o) => o.label === level);
    return opt ? opt.color : "#6ee7b7"; // default emerald
}

// ─── Review Modal ────────────────────────────────────────────────────────────
const BLANK = { place_name: "", category: "restaurant", city: "", country: "", rating: 5, tip: "", pricing: "$$", accessibility: "", safety: "" };

export function ReviewModal({ open, initialData, onClose, onSaved }) {
    const { t } = useTranslation();
    const [f, setF]   = useState(BLANK);
    const [photos, setPhotos] = useState([]);
    const [saving, setSaving] = useState(false);
    const [showCitySugg, setShowCitySugg] = useState(false);
    const [hoverRating, setHoverRating] = useState(0);
    const citySuggestions = useCitySuggestions(f.city, f.country);

    useEffect(() => {
        if (open && initialData) {
            setF({
                place_name:    initialData.place_name    || "",
                category:      initialData.category      || "restaurant",
                city:          initialData.city          || "",
                country:       initialData.country       || "",
                rating:        initialData.rating        || 5,
                tip:           initialData.tip           || "",
                pricing:       initialData.pricing       || "$$",
                accessibility: initialData.accessibility || "",
                safety:        initialData.safety        || "",
            });
            const existingPhotos = initialData.photos?.length
                ? initialData.photos
                : (initialData.photo_url ? [initialData.photo_url] : []);
            setPhotos(existingPhotos);
        } else if (open) {
            setF(BLANK);
            setPhotos([]);
        }
    }, [initialData, open]);

    const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!photos.length) { toast.error("Please add at least one photo."); return; }
        if (!f.country) { toast.error("Please select a country."); return; }
        if (!f.city.trim()) { toast.error("Please enter a city."); return; }
        setSaving(true);
        try {
            const payload = { ...f, rating: Number(f.rating), photos, photo_url: photos[0] };
            if (initialData) {
                await reviewsApi.update(initialData.id, payload);
            } else {
                await reviewsApi.create(payload);
            }
            toast.success(initialData ? "Review updated" : t("reviewsPanel.toasts.posted"));
            onSaved?.();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(detail || t("reviewsPanel.toasts.failed"));
        } finally { setSaving(false); }
    };

    const inputStyle = { width: "100%", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px", color: "white", fontSize: 14, transition: "border-color 0.3s ease" };
    const selectStyle = { ...inputStyle, cursor: "pointer", WebkitAppearance: "none", appearance: "none" };

    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                    style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }}
                    onClick={onClose}
                >
                    <motion.form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                        className="rounded-3xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto"
                        style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}
                        data-testid="review-modal"
                    >
                        <div className="overline mb-2">{t("reviewsPanel.modal.overline")}</div>
                        <h3 className="font-serif text-3xl text-white mb-6">
                            {initialData ? "Edit Review" : t("reviewsPanel.modal.title")}
                        </h3>

                        <div className="grid md:grid-cols-2 gap-4">
                            {/* Place name */}
                            <div className="md:col-span-2">
                                <span className="overline block mb-2">{t("reviewsPanel.modal.labels.placeName")}</span>
                                <input required value={f.place_name} onChange={set("place_name")}
                                    placeholder={t("reviewsPanel.modal.placeholders.placeName")}
                                    style={inputStyle} data-testid="review-place" />
                            </div>

                            {/* Category */}
                            <div>
                                <span className="overline block mb-2">{t("reviewsPanel.modal.labels.category")}</span>
                                <div className="relative">
                                    <select value={f.category} onChange={set("category")} style={selectStyle} data-testid="review-category">
                                        {CATEGORIES_CONFIG.map((c) => <option key={c.key} value={c.key} className="bg-slate-900 text-white">{t(c.labelKey, c.defaultLabel)}</option>)}
                                    </select>
                                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/40">▾</div>
                                </div>
                            </div>

                            {/* Pricing — fixed dropdown */}
                            <div>
                                <span className="overline block mb-2">{t("reviewsPanel.modal.labels.pricing")}</span>
                                <div className="flex gap-2 flex-wrap">
                                    {PRICING_OPTIONS.map((p) => (
                                        <button key={p} type="button"
                                            onClick={() => setF((s) => ({ ...s, pricing: p }))}
                                            className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                                            style={{
                                                background: f.pricing === p ? "rgba(212,175,55,0.15)" : "rgba(255,255,255,0.04)",
                                                border: `1px solid ${f.pricing === p ? "var(--gold)" : "rgba(255,255,255,0.1)"}`,
                                                color: f.pricing === p ? "var(--gold)" : "rgba(255,255,255,0.6)",
                                            }}
                                            data-testid={`review-pricing-${p}`}
                                        >{p}</button>
                                    ))}
                                </div>
                            </div>

                            {/* Country dropdown */}
                            <div>
                                <span className="overline block mb-2">{t("reviewsPanel.modal.labels.country")} <span className="text-red-400">*</span></span>
                                <div className="relative">
                                    <select
                                        required
                                        value={f.country}
                                        onChange={(e) => { setF((s) => ({ ...s, country: e.target.value, city: "" })); }}
                                        style={selectStyle}
                                        data-testid="review-country"
                                    >
                                        <option value="" disabled className="bg-slate-900 text-white/50">Select country…</option>
                                        {WORLD_COUNTRIES.map((c) => <option key={c} value={c} className="bg-slate-900 text-white">{c}</option>)}
                                    </select>
                                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/40">▾</div>
                                </div>
                            </div>

                            {/* City with autocomplete */}
                            <div className="relative">
                                <span className="overline block mb-2">{t("reviewsPanel.modal.labels.city")} <span className="text-red-400">*</span></span>
                                <input
                                    required
                                    disabled={!f.country}
                                    value={f.city}
                                    onChange={(e) => { setF((s) => ({ ...s, city: e.target.value })); setShowCitySugg(true); }}
                                    onFocus={() => setShowCitySugg(true)}
                                    placeholder={f.country ? "Type a city…" : "Select a country first"}
                                    style={{ ...inputStyle, opacity: f.country ? 1 : 0.5 }}
                                    data-testid="review-city"
                                    autoComplete="off"
                                />
                                <AnimatePresence>
                                    {showCitySugg && citySuggestions.length > 0 && (
                                        <motion.ul initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                                            className="absolute z-30 left-0 right-0 mt-1 rounded-2xl overflow-hidden shadow-2xl"
                                            style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.3)" }}
                                        >
                                            {citySuggestions.map((s) => (
                                                <li key={s} onMouseDown={(e) => { e.preventDefault(); setF((f) => ({ ...f, city: s })); setShowCitySugg(false); }}
                                                    className="px-4 py-2.5 hover:bg-white/5 cursor-pointer flex items-center gap-2 text-sm text-white">
                                                    <MapPin size={11} color="var(--gold)" />{s}
                                                </li>
                                            ))}
                                        </motion.ul>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Star rating */}
                            <div className="md:col-span-2">
                                <span className="overline block mb-2">{t("reviewsPanel.modal.labels.rating")}</span>
                                <div className="flex gap-2" onMouseLeave={() => setHoverRating(0)}>
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button key={n} type="button" 
                                            onClick={() => setF((s) => ({ ...s, rating: n }))}
                                            onMouseEnter={() => setHoverRating(n)}
                                            className="w-10 h-10 rounded-full grid place-items-center transition-colors"
                                            style={{
                                                background: n <= (hoverRating || f.rating) ? "rgba(212,175,55,0.15)" : "transparent",
                                                border: "1px solid " + (n <= (hoverRating || f.rating) ? "var(--gold)" : "rgba(255,255,255,0.1)"),
                                            }}
                                            data-testid={`review-rating-${n}`}
                                        >
                                            <Star size={16} weight={n <= (hoverRating || f.rating) ? "fill" : "regular"} color={n <= (hoverRating || f.rating) ? "#D4AF37" : "rgba(255,255,255,0.4)"} />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Tip / Review */}
                            <div className="md:col-span-2">
                                <span className="overline block mb-2">{t("reviewsPanel.modal.labels.tip", "Review / Experience")}</span>
                                <textarea required value={f.tip} onChange={set("tip")}
                                    placeholder={t("reviewsPanel.modal.placeholders.tip", "Share your experience, thoughts, or tips for others...")}
                                    style={{ ...inputStyle, resize: "vertical" }}
                                    rows={3} data-testid="review-tip" />
                            </div>

                            {/* Accessibility */}
                            <div>
                                <span className="overline block mb-2">{t("reviewsPanel.modal.labels.accessibility")}</span>
                                <input value={f.accessibility} onChange={set("accessibility")}
                                    placeholder={t("reviewsPanel.modal.placeholders.accessibility")}
                                    style={inputStyle} data-testid="review-accessibility" />
                            </div>

                            {/* Safety */}
                            <div>
                                <span className="overline block mb-2">{t("reviewsPanel.modal.labels.safety")}</span>
                                <div className="flex flex-wrap gap-2">
                                    {SAFETY_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.label}
                                            type="button"
                                            onClick={() => setF((p) => ({ ...p, safety: p.safety === opt.label ? "" : opt.label }))}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all"
                                            style={{
                                                background: f.safety === opt.label ? `${opt.color}22` : "rgba(255,255,255,0.04)",
                                                border: `1px solid ${f.safety === opt.label ? opt.color : "rgba(255,255,255,0.1)"}`,
                                                color: f.safety === opt.label ? opt.color : "rgba(255,255,255,0.45)",
                                            }}
                                            data-testid={`review-safety-${opt.label.replace(" ", "-").toLowerCase()}`}
                                        >
                                            <ShieldCheck size={11} weight={f.safety === opt.label ? "fill" : "regular"} />
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Photos — multi */}
                            <div className="md:col-span-2">
                                <span className="overline block mb-2">Photos <span className="text-red-400">*</span> (at least 1)</span>
                                <PhotoUploader photos={photos} onChange={setPhotos} />
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center" data-testid="review-cancel">
                                {t("common.cancel")}
                            </button>
                            <button type="submit" disabled={saving || photos.length === 0} className="btn-gold flex-1 justify-center" data-testid="review-save">
                                {saving ? "Saving…" : (initialData ? "Save Changes" : t("reviewsPanel.modal.postReview"))}
                            </button>
                        </div>
                    </motion.form>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
